import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { addressLine2Msg, cancelMsg, companyNameMsg, postOfficeMsg, readyMsg, t } from '../../../../common/i18n';
import { PhoneNumber, TextInput } from '../../../../common/react-hook-form/fields';
import { POSTAL_CODE_REGEX } from '../../../../common/utils/validationUtils.js';
import { PostalCodeInput } from '../../../PostalCodeInput/PostalCodeInput.js';
import { Search } from '../../../Search/Search.js';
import { useAddressSearch } from '../../hooks/useAddressSearch.js';
import type { DeliveryAddress } from '../../../../generated/api/deliveryAddress.js';

import '../../../../components/AddressSearch/AddressSearch.scss';

export interface DeliveryStepAddressCoCoProps {
  onSetAddress: (address?: DeliveryAddress, addressId?: string) => void;
  onCancel: () => void;
  companyName?: string;
  streetSearchAddressVal: string;
  postalCode: string;
  setPostalCode: (postalCode: string) => void;
  setStreetSearchAddressVal: (streetSearchAddressVal: string) => void;
  showCompanyNameInput?: boolean;
}

export const DeliveryStepAddressCoCo = ({
  onSetAddress,
  onCancel,
  companyName = '',
  streetSearchAddressVal,
  postalCode,
  setPostalCode,
  setStreetSearchAddressVal,
  showCompanyNameInput = false,
}: DeliveryStepAddressCoCoProps) => {
  const initialSearchAddressValue = streetSearchAddressVal ?? undefined;
  const streetAddressInput = useRef<HTMLInputElement>(null);
  const [validationErrors, setValidationErrors] = useState<{ [s: string]: string }>();

  const { handleSubmit, getValues, setValue } = useFormContext<DeliveryAddress>();

  const {
    apartments,
    addressSearchResult,
    matchedAddressId,
    postOffice,
    resetApartments,
    onSearchAddress,
    onSelectStreet,
    onSelectApartment,
    getDropdownItems,
  } = useAddressSearch({ postalCode, onDisplayTextChange: setStreetSearchAddressVal });

  useEffect(() => {
    if (postOffice) {
      setValue('address.postOffice', postOffice, { shouldValidate: true });
    }
  }, [postOffice, setValue]);

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  };

  const onSubmit = async () => {
    const formValues = getValues();
    const deliveryAddress: DeliveryAddress = {
      ...formValues,
      address: {
        ...formValues.address,
        line1: streetSearchAddressVal,
        postalCode,
        postOffice: formValues.address?.postOffice || postOffice,
      },
      companyName: showCompanyNameInput ? formValues.companyName : companyName,
    };
    onSetAddress(deliveryAddress, matchedAddressId);
  };

  const handleSetAddress = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  const handleSearchInput = (searchText: string) => {
    const trimmed = searchText.trim();
    const selectedApt = apartments.find(a => a.displayText === trimmed);
    if (selectedApt) {
      onSelectApartment(selectedApt);
      return;
    }
    const selectedStreet = addressSearchResult?.matchedAddresses?.find(a => a.addressText === trimmed);
    if (selectedStreet) {
      onSelectStreet(selectedStreet.addressId, selectedStreet.addressText);
      return;
    }
    onSearchAddress(trimmed, postalCode);
  };

  return (
    <CL.Grid className={dsClass.MARGIN_TOP_4}>
      {showCompanyNameInput && (
        <CL.GridRow>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <TextInput
              name="companyName"
              label={t.AJ93(companyNameMsg)}
              placeholder={t.AJ93(companyNameMsg)}
              required
              maxLength={100}
            />
          </CL.GridCol>
        </CL.GridRow>
      )}
      <CL.GridRow>
        <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
          <TextInput
            name="recipient"
            label={t.U9EI('Recipient Name')}
            placeholder={t.U9EI('Recipient Name')}
            required
            maxLength={140}
          />
        </CL.GridCol>
        <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
          <PhoneNumber name="phoneNumber" label={t.O5R1('Recipient Phone Number')} required />
        </CL.GridCol>
      </CL.GridRow>
      <CL.GridRow>
        <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
          <PostalCodeInput
            className={dsClass.POSITION_RELATIVE}
            postalCode={postalCode}
            setPostalCode={newCode => {
              setPostalCode(newCode);
              resetApartments();
            }}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
          />
        </CL.GridCol>
        <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
          <TextInput
            name="address.postOffice"
            label={t.Z7S5(postOfficeMsg)}
            placeholder={t.Z7S5(postOfficeMsg)}
            required
            maxLength={100}
          />
        </CL.GridCol>
      </CL.GridRow>
      <CL.GridRow>
        <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
          <Search
            value={streetSearchAddressVal}
            setValue={val => {
              setStreetSearchAddressVal(val);
              if (apartments.length > 0) {
                resetApartments();
              }
            }}
            disabled={!POSTAL_CODE_REGEX.test(postalCode) || false}
            emptyResultsLabel={t.J6P7('Enter the full address, including the door number')}
            inputLabel={t.GL4R('Street address, e.g. Ritarikatu 42 C 6')}
            initialValue={initialSearchAddressValue}
            inputPlaceholder={t.MX5U('Street address and number')}
            inputRef={streetAddressInput}
            minimumCharacters={3}
            onSearch={handleSearchInput}
            searchResults={getDropdownItems()}
            showSuccessIcon={Boolean(streetSearchAddressVal) && !validationErrors?.streetAddress}
            validationError={validationErrors?.streetAddress}
          />
        </CL.GridCol>
        <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
          <TextInput
            name="address.line2"
            label={t.O0QI(addressLine2Msg)}
            placeholder={t.O0QI(addressLine2Msg)}
            maxLength={40}
            required={false}
          />
        </CL.GridCol>
      </CL.GridRow>

      <CL.GridRow>
        <CL.GridCol colWidthXS={4}>
          <CL.Button
            type="button"
            onClick={handleSetAddress}
            disabled={!postalCode || !streetSearchAddressVal || Boolean(validationErrors)}
          >
            {t.L75K(readyMsg)}
          </CL.Button>
          <CL.Button
            type="button"
            onClick={handleCancel}
            color="secondary"
            className={`${dsClass.MARGIN_HORIZONTAL_3}`}
          >
            {t.B2V1(cancelMsg)}
          </CL.Button>
        </CL.GridCol>
      </CL.GridRow>
    </CL.Grid>
  );
};
